import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  Users,
  ChevronRight,
  ChevronLeft,
  Loader2,
  AlertCircle
} from 'lucide-react';


function formatMoney(value) {
  const numero = Number(value || 0);

  return numero.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}


function StatusBadge({ status }) {

  const valor = String(status || '').toLowerCase();

  const estilos = {
    pago: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    aberta: 'bg-amber-50 text-amber-700 border-amber-200',
    pendente: 'bg-amber-50 text-amber-700 border-amber-200',
    vencido: 'bg-red-50 text-red-700 border-red-200',
    atrasado: 'bg-red-50 text-red-700 border-red-200',
    cancelado: 'bg-slate-100 text-slate-500 border-slate-200',
    nao_gerada: 'bg-slate-100 text-slate-600 border-slate-200'
  };

  const nomes = {
    pago: 'Pago',
    aberta: 'Em aberto',
    pendente: 'Pendente',
    vencido: 'Vencido',
    atrasado: 'Atrasado',
    cancelado: 'Cancelado',
    nao_gerada: 'Não gerada'
  };

  return (
    <span
      className={[
        'inline-flex items-center px-2.5 py-1 rounded-full',
        'border text-xs font-semibold',
        estilos[valor] ||
        'bg-slate-100 text-slate-600 border-slate-200'
      ].join(' ')}
    >
      {nomes[valor] || status || '-'}
    </span>
  );
}


export default function ParceiroTurmasPage() {

  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const [turmaSelecionada, setTurmaSelecionada] = useState(null);
  const [alunos, setAlunos] = useState([]);
  const [loadingAlunos, setLoadingAlunos] = useState(false);
  const [erroAlunos, setErroAlunos] = useState('');


  useEffect(() => {

    carregarTurmas();

  }, []);


  async function carregarTurmas() {

    setLoading(true);
    setErro('');

    try {

      const response = await api.get('/parceiro/turmas');

      setTurmas(
        Array.isArray(response.data)
          ? response.data
          : []
      );

    } catch (error) {

      console.error(error);

      setErro(
        error?.response?.data?.detail ||
        'Não foi possível carregar suas turmas.'
      );

    } finally {

      setLoading(false);

    }
  }


  async function abrirTurma(turma) {

    setTurmaSelecionada(turma);

    setLoadingAlunos(true);
    setErroAlunos('');
    setAlunos([]);

    try {

      const response = await api.get(
        `/parceiro/turmas/${turma.id}/alunos`
      );

      setAlunos(
        Array.isArray(response.data)
          ? response.data
          : []
      );

    } catch (error) {

      console.error(error);

      setErroAlunos(
        error?.response?.data?.detail ||
        'Não foi possível carregar os alunos desta turma.'
      );

    } finally {

      setLoadingAlunos(false);

    }
  }


  function voltar() {

    setTurmaSelecionada(null);
    setAlunos([]);
    setErroAlunos('');

  }


  if (loading) {

    return (
      <div className="min-h-[400px] flex items-center justify-center">

        <div className="flex items-center gap-3 text-slate-500">

          <Loader2 className="w-5 h-5 animate-spin" />

          <span className="text-sm">
            Carregando turmas...
          </span>

        </div>

      </div>
    );
  }


  if (erro) {

    return (
      <div className="bg-white border border-red-200 rounded-2xl p-6">

        <div className="flex gap-3">

          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />

          <div>

            <h2 className="font-semibold text-slate-900">
              Não foi possível carregar
            </h2>

            <p className="text-sm text-slate-500 mt-1">
              {erro}
            </p>

            <Button
              onClick={carregarTurmas}
              className="mt-4 bg-slate-900 text-white hover:bg-slate-800"
            >
              Tentar novamente
            </Button>

          </div>

        </div>

      </div>
    );
  }


  if (turmaSelecionada) {

    return (

      <div className="space-y-6">

        <div>

          <Button
            variant="ghost"
            onClick={voltar}
            className="-ml-3 text-slate-600 hover:text-slate-950 hover:bg-slate-100"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Voltar para turmas
          </Button>

        </div>


        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">

          <div>

            <p className="text-xs font-bold tracking-[0.18em] uppercase text-slate-400">
              Turma
            </p>

            <h1 className="text-2xl sm:text-3xl font-bold text-slate-950 mt-1">
              {turmaSelecionada.nome}
            </h1>

            <p className="text-sm text-slate-500 mt-2">
              Consulte os alunos vinculados à sua parceria.
            </p>

          </div>


          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl">

            <Users className="w-4 h-4 text-slate-500" />

            <span className="text-sm font-semibold text-slate-700">
              {turmaSelecionada.quantidade_alunos || 0}
              {' '}
              {(turmaSelecionada.quantidade_alunos || 0) === 1
                ? 'aluno'
                : 'alunos'}
            </span>

          </div>

        </div>


        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">

          {loadingAlunos ? (

            <div className="min-h-[260px] flex items-center justify-center">

              <div className="flex items-center gap-3 text-slate-500">

                <Loader2 className="w-5 h-5 animate-spin" />

                <span className="text-sm">
                  Carregando alunos...
                </span>

              </div>

            </div>

          ) : erroAlunos ? (

            <div className="p-6">

              <div className="flex gap-3">

                <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />

                <p className="text-sm text-slate-600">
                  {erroAlunos}
                </p>

              </div>

            </div>

          ) : alunos.length === 0 ? (

            <div className="py-16 px-6 text-center">

              <Users className="w-9 h-9 text-slate-300 mx-auto" />

              <h3 className="text-sm font-semibold text-slate-800 mt-4">
                Nenhum aluno nesta turma
              </h3>

              <p className="text-sm text-slate-500 mt-1">
                Ainda não há alunos vinculados.
              </p>

            </div>

          ) : (

            <>
              {/* DESKTOP */}

              <div className="hidden md:block overflow-x-auto">

                <table className="w-full">

                  <thead>

                    <tr className="border-b border-slate-200 bg-slate-50">

                      <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                        Nome
                      </th>

                      <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                        Plano
                      </th>

                      <th className="text-right px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                        Mensalidade
                      </th>

                      <th className="text-right px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                        Situação
                      </th>

                    </tr>

                  </thead>


                  <tbody>

                    {alunos.map((aluno, index) => (

                      <tr
                        key={`${aluno.nome}-${index}`}
                        className="border-b border-slate-100 last:border-0"
                      >

                        <td className="px-6 py-4">

                          <span className="text-sm font-semibold text-slate-900">
                            {aluno.nome}
                          </span>

                        </td>


                        <td className="px-6 py-4">

                          <span className="text-sm text-slate-600">
                            {aluno.plano || '-'}
                          </span>

                        </td>


                        <td className="px-6 py-4 text-right">

                          <span className="text-sm font-semibold text-slate-900">
                            {formatMoney(aluno.valor_mensalidade)}
                          </span>

                        </td>


                        <td className="px-6 py-4 text-right">

                          <StatusBadge
                            status={aluno.situacao}
                          />

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>


              {/* MOBILE */}

              <div className="md:hidden divide-y divide-slate-100">

                {alunos.map((aluno, index) => (

                  <div
                    key={`${aluno.nome}-${index}`}
                    className="p-5"
                  >

                    <div className="flex items-start justify-between gap-3">

                      <div className="min-w-0">

                        <h3 className="text-sm font-semibold text-slate-900 truncate">
                          {aluno.nome}
                        </h3>

                        <p className="text-xs text-slate-500 mt-1">
                          {aluno.plano || '-'}
                        </p>

                      </div>

                      <StatusBadge
                        status={aluno.situacao}
                      />

                    </div>


                    <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between">

                      <span className="text-xs text-slate-500">
                        Mensalidade
                      </span>

                      <span className="text-sm font-semibold text-slate-900">
                        {formatMoney(aluno.valor_mensalidade)}
                      </span>

                    </div>

                  </div>

                ))}

              </div>

            </>

          )}

        </div>

      </div>
    );
  }


  return (

    <div className="space-y-7">

      <div>

        <p className="text-xs font-bold tracking-[0.18em] uppercase text-slate-400">
          Portal do Parceiro
        </p>

        <h1 className="text-2xl sm:text-3xl font-bold text-slate-950 mt-1">
          Minhas turmas
        </h1>

        <p className="text-sm sm:text-base text-slate-500 mt-2 max-w-2xl">
          Acompanhe as turmas vinculadas à sua parceria e consulte os alunos de cada uma.
        </p>

      </div>


      {turmas.length === 0 ? (

        <div className="bg-white border border-slate-200 rounded-2xl py-16 px-6 text-center">

          <BookOpen className="w-10 h-10 text-slate-300 mx-auto" />

          <h2 className="font-semibold text-slate-900 mt-4">
            Nenhuma turma vinculada
          </h2>

          <p className="text-sm text-slate-500 mt-1">
            Não existem turmas disponíveis para esta parceria.
          </p>

        </div>

      ) : (

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

          {turmas.map((turma) => (

            <button
              key={turma.id}
              type="button"
              onClick={() => abrirTurma(turma)}
              className="group text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 hover:shadow-md transition-all duration-200"
            >

              <div className="flex items-start justify-between gap-4">

                <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center">

                  <BookOpen className="w-5 h-5 text-slate-700" />

                </div>

                <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-600 transition-colors" />

              </div>


              <div className="mt-6">

                <h2 className="text-lg font-bold text-slate-950">
                  {turma.nome}
                </h2>

                <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">

                  <Users className="w-4 h-4" />

                  <span>
                    {turma.quantidade_alunos || 0}
                    {' '}
                    {(turma.quantidade_alunos || 0) === 1
                      ? 'aluno'
                      : 'alunos'}
                  </span>

                </div>

              </div>


              <div className="mt-6 pt-4 border-t border-slate-100">

                <span className="text-xs font-semibold text-slate-500 group-hover:text-slate-900 transition-colors">
                  Ver alunos
                </span>

              </div>

            </button>

          ))}

        </div>

      )}

    </div>
  );
}
